import axios from "axios";
import Cookies from 'js-cookie';
import { persistor } from '../services/store/store'
import { removeWorkSpaceTab } from "../utils/helper/helper";
import reportFrontendIncident from "../monitoring/reportFrontendIncident";
import * as Breadcrumbs from "../monitoring/breadcrumbs";

const LOOPBACK_URL_REGEX = /^https?:\/\/(?:127\.0\.0\.1|localhost)(?::\d+)?(\/.*)$/i;
const UUID_SEGMENT_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const BACKGROUND_API_FAILURE_THRESHOLD = 5;
const backgroundApiFailures = new Map();

const QUIET_BACKGROUND_API_ENDPOINTS = [
	{ method: 'GET', pathSuffix: '/delivery-video-calls/pending' },
	{ method: 'POST', pathSuffix: '/realtime/presence/heartbeat' },
	{ method: 'POST', pathSuffix: '/realtime/presence/disconnect' },
];

function sanitizeEndpoint(config = {}) {
	try {
		const rawUrl = String(config.url || '');
		const baseUrl = config.baseURL || window.location.origin;
		const parsed = new URL(rawUrl, baseUrl);
		return parsed.pathname
			.split('/')
			.map((segment) => {
				if (/^\d+$/.test(segment) || UUID_SEGMENT_REGEX.test(segment)) {
					return ':id';
				}

				return segment;
			})
			.join('/');
	} catch (_) {
		return String(config.url || 'unknown-endpoint').split('?')[0];
	}
}

function getSafeResponseMessage(error) {
	const responseMessage = error?.response?.data?.message;
	if (typeof responseMessage === 'string' && responseMessage.trim()) {
		return responseMessage.trim().slice(0, 500);
	}

	if (typeof error?.message === 'string' && error.message.trim()) {
		return error.message.trim().slice(0, 500);
	}

	return 'API request failed';
}

function backgroundApiFailureKey(method, endpoint) {
	return `${method}:${endpoint}`;
}

function quietBackgroundApiMatch(method, endpoint) {
	return QUIET_BACKGROUND_API_ENDPOINTS.some((entry) =>
		entry.method === method && endpoint.endsWith(entry.pathSuffix)
	);
}

function shouldReportBackgroundApiFailure(method, endpoint) {
	if (!quietBackgroundApiMatch(method, endpoint)) {
		return true;
	}

	const key = backgroundApiFailureKey(method, endpoint);
	const failureCount = (backgroundApiFailures.get(key) || 0) + 1;
	backgroundApiFailures.set(key, failureCount);

	return failureCount >= BACKGROUND_API_FAILURE_THRESHOLD
		&& failureCount % BACKGROUND_API_FAILURE_THRESHOLD === 0;
}

function resetBackgroundApiFailure(config) {
	const method = String(config?.method || 'GET').toUpperCase();
	const endpoint = sanitizeEndpoint(config);

	if (quietBackgroundApiMatch(method, endpoint)) {
		backgroundApiFailures.delete(backgroundApiFailureKey(method, endpoint));
	}
}

function shouldReportApiError(error) {
	if (axios.isCancel?.(error) || error?.code === 'ERR_CANCELED') {
		return false;
	}

	const endpoint = sanitizeEndpoint(error?.config);
	if (endpoint.includes('/frontend-incidents')) {
		return false;
	}

	const status = error?.response?.status;
	return !status || status >= 500;
}

function reportApiIncident(error) {
	if (!shouldReportApiError(error)) {
		return;
	}

	const status = error?.response?.status ?? null;
	const method = String(error?.config?.method || 'GET').toUpperCase();
	const endpoint = sanitizeEndpoint(error?.config);

	if (!shouldReportBackgroundApiFailure(method, endpoint)) {
		return;
	}

	reportFrontendIncident({
		type: 'api-error',
		severity: status && status < 500 ? 'warning' : 'error',
		message: `${method} ${endpoint} failed${status ? ` with status ${status}` : ''}`,
		module_name: 'pharmacy-api',
		metadata: {
			status,
			method,
			endpoint,
			error_code: error?.code || null,
			background_failure_count: backgroundApiFailures.get(backgroundApiFailureKey(method, endpoint)) || null,
			background_failure_threshold: quietBackgroundApiMatch(method, endpoint)
				? BACKGROUND_API_FAILURE_THRESHOLD
				: null,
			response_message: getSafeResponseMessage(error),
		},
	});
}

const normalizeLoopbackUrls = (value) => {
	if (typeof value === 'string') {
		const matched = value.match(LOOPBACK_URL_REGEX);
		return matched ? matched[1] : value;
	}

	// Files (a PDF, an image, a download) pass through untouched: walking a Blob's keys would
	// rebuild it as {} and the viewer would receive an empty, broken "file".
	if (
		(typeof Blob !== 'undefined' && value instanceof Blob) ||
		(typeof ArrayBuffer !== 'undefined' && (value instanceof ArrayBuffer || ArrayBuffer.isView(value)))
	) {
		return value;
	}

	if (Array.isArray(value)) {
		return value.map((item) => normalizeLoopbackUrls(item));
	}

	if (value && typeof value === 'object') {
		const normalized = {};
		Object.keys(value).forEach((key) => {
			normalized[key] = normalizeLoopbackUrls(value[key]);
		});
		return normalized;
	}

	return value;
};

const Axios = axios.create({
	baseURL: process.env.REACT_APP_BASE_URL
});

Axios.interceptors.request.use(
	(config) => {
		const token = Cookies.get("authToken");
		const payrollChecksToken = Cookies.get("payroll_checks_token");
		const financialChecksToken = Cookies.get("financial_checks_token");
		const appUploadFinancialToken = Cookies.get("app_upload_financial_checks_token");

		// Check if we're on bookkeepers, accounts, or upload-bills page
		const isAdminPayrollPage = window.location.pathname.includes('bookkeepers') ||
			window.location.pathname.includes('accounts') ||
			window.location.pathname.includes('upload-bills');

		// Get type parameter from URL query string
		const urlParams = new URLSearchParams(window.location.search);
		const typeParam = urlParams.get('type');


		const isFormData = config.data instanceof FormData;

		config.headers["Accept"] = "application/json";

		// For FormData, let the browser set the multipart boundary automatically.
		if (isFormData) {
			delete config.headers["Content-Type"];
		} else {
			config.headers["Content-Type"] = "application/json";
		}

		// Super Nova logout must use ONLY the OTP session token — never the app JWT (causes "Invalid token type.").
		const urlForMatch = String(config.url || '');
		const baseAndUrl = `${config.baseURL || ''}${urlForMatch}`;
		const isSupernovaLogout =
			urlForMatch.includes('supernova/logout') || baseAndUrl.includes('supernova/logout');

		if (isSupernovaLogout) {
			const supernovaSessionToken = Cookies.get('supernova_checks_token');
			delete config.headers['Authorization'];
			delete config.headers['authorization'];
			if (supernovaSessionToken) {
				const clean = String(supernovaSessionToken).replace(/^Bearer\s+/i, '').trim();
				if (clean) {
					config.headers['Authorization'] = `Bearer ${clean}`;
				}
			}
		} else if (token) {
			config.headers["Authorization"] = `Bearer ${token}`;
		}

		// Add token header based on type parameter or route
		// If on upload-bills page -> use app_upload_financial_checks_token
		// If type=accounting -> use financial_checks_token
		// Otherwise use X-Checks-Token for payroll
		if (isAdminPayrollPage) {
			const isUploadBillsPage = window.location.pathname.includes('upload-bills');
			if (isUploadBillsPage && appUploadFinancialToken) {
				// Use app_upload token for upload-bills page
				config.headers["X-Finance-Token"] = appUploadFinancialToken;
			} else if (typeParam === 'accounting' && financialChecksToken) {
				// Use existing financial token for accounting pages
				config.headers["X-Finance-Token"] = financialChecksToken;
			} else if (payrollChecksToken) {
				config.headers["X-Checks-Token"] = payrollChecksToken;
			}
		}

		return config;
	},
	(error) => {
		return Promise.reject(error);
	}
);

function recordApiBreadcrumb(config, status) {
	// Endpoint + status only — request/response bodies are never touched.
	try {
		if (!config) return;
		const method = String(config.method || 'get').toUpperCase();
		Breadcrumbs.api(`${method} ${sanitizeEndpoint(config)} -> ${status}`);
	} catch (_) {
		/* never break a request */
	}
}

Axios.interceptors.response.use(
	(response) => {
		recordApiBreadcrumb(response?.config, response?.status);
		resetBackgroundApiFailure(response?.config);
		if (process.env.NODE_ENV === 'development' && response?.data) {
			response.data = normalizeLoopbackUrls(response.data);
		}
		return response
	},
	(error) => {
		recordApiBreadcrumb(error?.config, error?.response?.status || 'network-error');
		reportApiIncident(error);

		if (error?.response?.status === 401) {
			// Don't redirect if we're on upload-bills page - let the component handle logout
			const isUploadBillsPage = window.location.pathname.includes('upload-bills');
			if (isUploadBillsPage) {
				// On upload-bills page, just reject the error - component will handle logout
				return Promise.reject(error);
			}

			if (typeof BroadcastChannel !== 'undefined') {
				const broadcastChannel = new BroadcastChannel("logout-channel");
				broadcastChannel.postMessage("logout");
			}

			// Clear all cookies
			Object.keys(Cookies.get()).forEach(cookieName => {
				Cookies.remove(cookieName);
			});

			// Clear all sessionStorage items
			sessionStorage.clear();

			// Cookies.remove('authToken')
			removeWorkSpaceTab()
			persistor.purge()
			window.location.href = '/'
			localStorage.removeItem('@oat_p$(sDe)');
		}
		if ((error?.response?.status === 403 && error?.response?.data?.message === 'Checks verification required.') ||
			(error?.response?.status === 403 && error?.response?.data?.message === 'Financial checks verification required.')
		) {
			// Clear payroll cookies
			Cookies.remove('payroll_otp_verified')
			Cookies.remove('payroll_checks_token')
			Cookies.remove('payroll_seed_user')

			Cookies.remove('financial_otp_verified')
			Cookies.remove('financial_checks_token')
			Cookies.remove('financial_seed_user')
			// // Reload page to reflect cookie changes
			setTimeout(() => {
				window.location.reload()
			}, 1000)

		}
		return Promise.reject(error);
	}
);

export default Axios;
